import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Icon } from '../Icon';
import styles from './ProgressLoader.module.scss';

interface ProgressLoaderProps {
  active: boolean;
  done?: boolean;
  mode?: 'progress' | 'spinner';
  fullScreen?: boolean;
  color?: 'white' | 'grey';
}

export const ProgressLoader: React.FC<ProgressLoaderProps> = ({
  active,
  fullScreen = true,
  color = 'white',
  done = false,
  mode = 'progress',
}) => {
  const [progress, setProgress] = useState(10);
  const targetRef = useRef(10);
  const rafRef = useRef<number | null>(null);
  const timerRef = useRef<number | null>(null);

  // завершение: для progress — 100%, для spinner — done=true
  const progressComplete = mode === 'progress' && progress >= 100;
  const spinnerComplete = mode === 'spinner' && done;
  const isComplete = progressComplete || spinnerComplete;

  useEffect(() => {
    if (!active) return;
    if (mode !== 'progress') return;

    targetRef.current = 15;
    setProgress(10);

    const animate = () => {
      setProgress((p) => {
        const target = targetRef.current;
        const next = p + (target - p) * 0.12;
        if (Math.abs(target - next) < 0.2) {
          return target;
        }
        rafRef.current = requestAnimationFrame(animate);
        return next;
      });
    };

    rafRef.current = requestAnimationFrame(animate);

    timerRef.current = window.setInterval(() => {
      if (!done && targetRef.current < 95) {
        targetRef.current = Math.min(95, targetRef.current + 3);
      }
    }, 120);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [active, done, mode]);

  useEffect(() => {
    if (mode === 'progress' && done) {
      targetRef.current = 100;
      window.setTimeout(() => setProgress(100), 200);
    }
  }, [done, mode]);

  if (!active) return null;

  const size = 19;
  const strokeWidth = 3;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - 0.3 * circumference;

  let boxClassName = styles.box;
  boxClassName += isComplete
    ? ` ${styles.complete}`
    : ` ${styles.loadingPulse}`;

  let content: React.ReactNode = null;
  if (mode === 'spinner') {
    content = spinnerComplete ? (
      <div className={styles.checkIconWrapper}>
        <Icon
          className={styles.iconCheck}
          name="outlinedCheck"
          width={24}
          height={24}
        />
      </div>
    ) : (
      <div className={styles.spinner} />
    );
  } else if (isComplete) {
    content = (
      <div className={styles.checkIconWrapper}>
        <Icon
          className={styles.iconCheck}
          name="outlinedCheck"
          width={24}
          height={24}
        />
      </div>
    );
  } else {
    content = (
      <svg
        width={size}
        height={size}
        className={classNames(styles.svg, styles[color])}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          className={styles.track}
          strokeWidth={strokeWidth}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          className={styles.indicator}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />

      </svg>
    );
  }

  if (!fullScreen) {
    return content;
  }

  const node = (
    <div className={styles.backdrop} aria-live="polite" aria-busy>
      <div className={boxClassName}>
        <div className={styles.inner}>{content}</div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return node;
  return createPortal(node, document.body);
};
